import React, {createContext, ReactNode, useContext, useEffect, useMemo, useState} from "react";
import {AvailableMenu, Product} from "@/data/entities/menu";
import {MatchingFulfillmentType} from "@/data/entities/product";
import {SetStateFn} from "@/utils/SetStateUtils";
import {ProductSelection} from "@/data/entities/cart";
import {useFulfillmentType} from "@widgets/pos/order/hooks/UseFulfillmentType";
import {generateInitialProductSelection} from "@/data/entities/cart/utils";
import {getMatchingFulfillmentType} from "@/data/entities/product/utils";

type ProductInfoContextType = {
    product: Product,
    setProduct: (product: Product) => void,
    matchingFulfillmentType: MatchingFulfillmentType,
    canAddToBasket: boolean,
    loading: boolean,
    setLoading: SetStateFn<boolean>,
    disabled: boolean,
    setDisabled: SetStateFn<boolean>,
    show: boolean,
    setShow: (show: boolean) => void,
    productSelection: ProductSelection,
    setProductSelection: SetStateFn<ProductSelection>,
    isAlcoholic: boolean,
};

const ProductInfoContext = createContext<ProductInfoContextType>({} as ProductInfoContextType);

type Props = {
    availableMenu: AvailableMenu,
    product: Product,
    setProduct: (product: Product) => void,
    show: boolean,
    setShow: (show: boolean) => void,
    children: ReactNode,
};

export const ProductInfoProvider = ({
                                        availableMenu,
                                        product,
                                        setProduct,
                                        show,
                                        setShow,
                                        children,
                                    }: Props) => {
    const fulfillmentType = useFulfillmentType();
    const [loading, setLoading] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [productSelection, setProductSelection] = useState<ProductSelection>(
        generateInitialProductSelection(product, availableMenu));
    const matchingFulfillmentType: MatchingFulfillmentType = useMemo(
        () => getMatchingFulfillmentType(fulfillmentType, product.availability),
        [fulfillmentType, product.availability]);
    const isAlcoholic = product.flags.includes("alcoholic");
    const {soldout} = product;
    const {match: fulfillmentTypeMatch} = matchingFulfillmentType;
    const canAddToBasket: boolean = useMemo(
        () => !loading
            && !disabled
            && !soldout
            && fulfillmentTypeMatch,
        [loading, disabled, soldout, fulfillmentTypeMatch]);

    // Reset product to add after changing product or hiding/showing modal
    useEffect(() => {
        if (!show) {
            return;
        }

        setProductSelection(generateInitialProductSelection(product, availableMenu));
    }, [show, availableMenu, product]);

    return (
        <ProductInfoContext.Provider
            value={{
                product,
                setProduct,
                matchingFulfillmentType,
                loading,
                disabled,
                canAddToBasket,
                setLoading,
                setDisabled,
                show,
                setShow,
                productSelection,
                setProductSelection,
                isAlcoholic,
            }}
        >
            {children}
        </ProductInfoContext.Provider>
    );
}

const useProductInfo = () => {
    const ctx = useContext(ProductInfoContext);
    if (!ctx) {
        throw new Error("useProductInfo must be used inside ProductInfoProvider");
    }

    return ctx;
}

export default useProductInfo;
